import { useRef, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import Text from "../components/Text";
import { Sidebar } from "primereact/sidebar";
import InvestorProfileForm from "../components/InvestorProfileForm";
import { useGetInvestorProfile, useGetInvestorProfileInsights } from "../api/investor-profile-api";
import { useAuthContext } from "../contexts/AuthContext";
import moment from "moment";
import { useMutation } from "@tanstack/react-query";
import useAuthHttp from "../hooks/useAuthHttp";
import { Toast } from "primereact/toast";
import InsightsSidebar from "../components/InsightsSidebar";
import Show from "../components/Show";
import Container from "../components/Container";
import FormField from "../components/FormField";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { Password } from "primereact/password";
import { InputOtp } from "primereact/inputotp";

// Versão adaptada para PrimeReact + PrimeFlex
// Cor principal: #0265F9 (primary)

export default function PerfilUsuario() {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showEditProfile, setShowEditProfile] = useState(false);
    const investorProfileQuery = useGetInvestorProfile();
    const investorProfileInsightsQuery = useGetInvestorProfileInsights();
    const auth = useAuthContext();
    const authHttp = useAuthHttp();
    const toastRef = useRef<Toast|null>(null);
    const [showInsightsSidebar, setShowInsightsSidebar] = useState(false);
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

    const requestForgotMutation = useMutation({
        mutationFn: async (email: string) => {
            return axios.post(`${import.meta.env.VITE_API_URL}/user/forgot_password`, 
                { 
                    username: email 
                },
                {
                    headers: {
                        "Content-Type": "text/plain"
                    }
                });
        }
    });

    const requestChangePasswordMutation = useMutation({
        mutationFn: async ({email, password, code} :{ email: string, password: string, code: string }) => {
            return axios.post(`${import.meta.env.VITE_API_URL}/user/reset_password`, 
                { 
                    username: email,
                    password: password,
                    code: code
                },
                {
                    headers: {
                        "Content-Type": "text/plain"
                    }
                });
        },
        onSuccess: () => {
            toastRef.current?.show({
                severity: "success",
                summary: "Senha alterada com sucesso",
            });

            setTimeout(() => {
                setShowChangePasswordModal(false);
                formik.resetForm();
            }, 2000);
        },
        onError: (error) => {
            toastRef.current?.show({
                severity: "error",
                summary: error.message,
            });
        }
    });

    const formik = useFormik({
        initialValues: {
            password: "",
            code: "",
        },
        validationSchema: Yup.object().shape({
            password: Yup.string().required("Senha é obrigatória"),
            code: Yup.string().required("Código de verificação é obrigatório"),
        }),
        onSubmit: async (values) => {
            await requestChangePasswordMutation.mutateAsync({
                email: auth.user.email,
                password: values.password,
                code: values.code
            });
        },
    });

    const deleteAccountMutation = useMutation({
        mutationFn: async () => {
            return await authHttp.doDelete(`/user/${auth.user.user_id}`);
        }
    });

    const primary = "#0265F9";

    const radius = 36;
    const circumference = 2 * Math.PI * radius;
    const progress = Math.min(Math.max(Number(investorProfileQuery.data?.score_percentage.toFixed(2)) ?? 0, 0), 100);
    const offset = circumference - (progress / 100) * circumference;

    return (
        <>
            <Toast ref={toastRef} />

            <InsightsSidebar
                insightsOpen={showInsightsSidebar}
                setInsightsOpen={setShowInsightsSidebar}
                setInvestorProfileOpen={setShowEditProfile}
            />

            <div className="flex justify-content-center w-full bg-gray-100 min-h-screen">
                <div className="w-full">
                    {/* Header */}
                    <div className="flex justify-content-between align-items-center p-4 bg-white border-round shadow-2">
                        <div className="flex align-items-center gap-3">
                            <div
                            className="border-circle flex align-items-center justify-content-center text-white text-xl font-bold"
                            style={{ width: 70, height: 70, background: primary }}
                            >
                            {auth.user.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                            </div>
                            <div>
                            <h2 className="text-2xl font-bold m-0">{auth.user.name}</h2>
                            <p className="text-sm text-600 m-0">{auth.user.email}</p>
                            </div>
                        </div>

                        <div className="flex gap-2">
                            <Button
                                label="Alterar senha"
                                severity="secondary"
                                outlined
                                onClick={() => setShowChangePasswordModal(true)}
                            />

                            <Button
                                label="Excluir conta"
                                severity="danger"
                                outlined
                                onClick={() => setShowDeleteModal(true)}
                            />
                        </div>
                    </div>

                    {/* Perfil do Investidor */}
                    <div className="mt-4">
                        <div className="flex flex-column justify-content-between align-items-center p-4 bg-white border-round shadow-2 w-full">
                            <h3 className="m-0 text-xl font-semibold">Perfil do Investidor</h3>

                            <Show when={investorProfileQuery.isFetching}>
                                <Container>
                                    <Text>Carregando...</Text>
                                </Container>
                            </Show>

                            <Show when={investorProfileQuery.isError || !investorProfileQuery.data?.profile}>
                                <Container flexColumn gap={2} marginVertical="4" center>
                                    <Text size="2xl" center>⚠️</Text>
                                    <Text color="900">Perfil do investidor ainda não foi cadastrado</Text>
                                    <Text size="sm">Clique no botão abaixo para cadastrar</Text>
                                    <Button
                                        onClick={() => setShowEditProfile(true)}
                                        className="justify-content-center w-full mt-4"
                                    >
                                        Cadastrar perfil
                                    </Button>
                                </Container>
                            </Show>

                            <Show when={investorProfileQuery.isSuccess && !!investorProfileQuery.data?.profile}>
                                <p className="text-sm text-600 mt-1 mb-3">Status atual e score</p>

                                <div className="flex flex-column md:flex-row justify-content-between align-items-center gap-4">
                                    <div className="flex flex-column align-items-center">
                                        <svg width="92" height="92" viewBox="0 0 100 100">
                                        <g transform="translate(50,50)">
                                            <circle r={radius} fill="#eee" />
                                            <circle
                                            r={radius}
                                            fill="transparent"
                                            stroke={primary}
                                            strokeWidth={8}
                                            strokeDasharray={`${circumference} ${circumference}`}
                                            strokeDashoffset={offset}
                                            strokeLinecap="round"
                                            transform={`rotate(-90)`}
                                            />
                                            <text x="0" y="6" textAnchor="middle" fontSize="16" fontWeight={700} fill="#111827">
                                            {progress}%
                                            </text>
                                        </g>
                                        </svg>
                                        <span className="text-xs text-600">Score do perfil</span>
                                    </div>

                                    <div className="text-right">
                                        <span
                                            className="px-3 py-1 border-round text-white text-sm font-medium"
                                            style={{ background: primary }}
                                        >
                                            {investorProfileQuery.data?.profile?.slice(0, 1).toUpperCase() ?? ""}{investorProfileQuery.data?.profile?.slice(1).toLowerCase()}
                                        </span>

                                        <p className="text-sm text-600 mt-3">
                                            Atualizado: {moment(investorProfileInsightsQuery.data?.portfolio_insight?.last_update).format("DD/MM/YYYY HH:mm")}
                                        </p>
                                    </div>
                                </div>

                                <p className="text-sm mt-3">
                                    {investorProfileInsightsQuery.data?.portfolio_insight?.openai_insight?.status}
                                </p>

                                <div className="flex gap-2 mt-4">
                                    <Button
                                        label="Atualizar perfil"
                                        onClick={() => setShowEditProfile(true)}
                                        outlined
                                    />
                                    <Button label="Ver insights" severity="secondary" outlined onClick={() => setShowInsightsSidebar(true)} />
                                </div>
                            </Show>
                        </div>
                    </div>

                    {/* Card de Links Adicionais */}
                    <div className="mt-4">
                        <div className="flex flex-column p-4 bg-white border-round shadow-2 w-full">
                            <h3 className="m-0 text-xl font-semibold mb-4">Informações</h3>
                            
                            <div className="flex flex-column gap-3">
                                <a
                                    href="https://www.wings.money/politica"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex align-items-center gap-3 p-3 border-1 border-round border-200 hover:border-primary hover:bg-blue-50 transition-all transition-duration-200 cursor-pointer no-underline"
                                    style={{ textDecoration: 'none', color: 'inherit' }}
                                >
                                    <div
                                        className="flex align-items-center justify-content-center border-circle"
                                        style={{ width: 40, height: 40, background: '#E3F2FD' }}
                                    >
                                        <i className="pi pi-shield text-primary" style={{ fontSize: '1.25rem' }}></i>
                                    </div>
                                    <div className="flex-1">
                                        <p className="m-0 font-semibold text-900">Política de Privacidade</p>
                                        <p className="m-0 text-sm text-600">Conheça nossa política de privacidade</p>
                                    </div>
                                    <i className="pi pi-external-link text-500"></i>
                                </a>

                                <a
                                    href="https://www.wings.money/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex align-items-center gap-3 p-3 border-1 border-round border-200 hover:border-primary hover:bg-blue-50 transition-all transition-duration-200 cursor-pointer no-underline"
                                    style={{ textDecoration: 'none', color: 'inherit' }}
                                >
                                    <div
                                        className="flex align-items-center justify-content-center border-circle"
                                        style={{ width: 40, height: 40, background: '#E3F2FD' }}
                                    >
                                        <i className="pi pi-info-circle text-primary" style={{ fontSize: '1.25rem' }}></i>
                                    </div>
                                    <div className="flex-1">
                                        <p className="m-0 font-semibold text-900">Sobre a Wings</p>
                                        <p className="m-0 text-sm text-600">Saiba mais sobre a Wings Money</p>
                                    </div>
                                    <i className="pi pi-external-link text-500"></i>
                                </a>

                                <a
                                    href="https://www.instagram.com/wings.money.app/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex align-items-center gap-3 p-3 border-1 border-round border-200 hover:border-primary hover:bg-blue-50 transition-all transition-duration-200 cursor-pointer no-underline"
                                    style={{ textDecoration: 'none', color: 'inherit' }}
                                >
                                    <div
                                        className="flex align-items-center justify-content-center border-circle"
                                        style={{ width: 40, height: 40, background: '#E3F2FD' }}
                                    >
                                        <i className="pi pi-instagram text-primary" style={{ fontSize: '1.25rem' }}></i>
                                    </div>
                                    <div className="flex-1">
                                        <p className="m-0 font-semibold text-900">Instagram</p>
                                        <p className="m-0 text-sm text-600">Siga-nos no Instagram</p>
                                    </div>
                                    <i className="pi pi-external-link text-500"></i>
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* Modal de Alteração de Senha */}
                    <Dialog
                        header="Alterar senha"
                        visible={showChangePasswordModal}
                        style={{ width: "25rem" }}
                        modal
                        onHide={() => setShowChangePasswordModal(false)}
                    >
                        <Show when={requestForgotMutation.isSuccess || requestForgotMutation.isPending}>
                            <div className="mb-4">
                                <Text>Um código de verificação foi enviado para seu email</Text>
                                <Button
                                    label="Reenviar código de verificação"
                                    severity="info"
                                    className="w-full"
                                    onClick={() => requestForgotMutation.mutate(auth.user.email)}
                                    loading={requestForgotMutation.isPending}
                                    disabled={requestForgotMutation.isPending}
                                />
                            </div>
                        </Show>

                        <Show when={!requestForgotMutation.submittedAt || (requestForgotMutation.isPending && !requestForgotMutation.submittedAt)}>
                            <div className="mb-4 flex flex-column gap-4">
                                <Text>Clique abaixo para receber um código de verificação</Text>
                                <Button
                                    label="Enviar código de verificação"
                                    severity="info"
                                    className="w-full"
                                    onClick={() => requestForgotMutation.mutate(auth.user.email)}
                                    loading={requestForgotMutation.isPending}
                                    disabled={requestForgotMutation.isPending}
                                />
                            </div>
                        </Show>

                        <Show when={requestForgotMutation.isSuccess}>
                            <div className="flex flex-column gap-4">
                                <FormField
                                    label="Nova senha"
                                    id="password"
                                    value={formik.values.password}
                                    error={formik.errors.password}
                                    hasError={!!formik.errors.password && !!formik.touched.password}
                                >
                                    <Password 
                                        value={formik.values.password}
                                        onChange={(e) => formik.setFieldValue("password", e.target.value)}
                                        invalid={!!formik.errors.password && !!formik.touched.password} 
                                        toggleMask
                                        className="w-full"
                                    />
                                </FormField>

                                <FormField
                                    label="Código de verificação"
                                    id="code"
                                    value={formik.values.code}
                                    error={formik.errors.code}
                                    hasError={!!formik.errors.code && !!formik.touched.code}
                                >
                                    <InputOtp 
                                        value={formik.values.code}
                                        onChange={(e) => formik.setFieldValue("code", e.value)}
                                        invalid={!!formik.errors.code && !!formik.touched.code}
                                        length={6}
                                    />
                                </FormField>

                                <Button
                                    label="Alterar senha"
                                    severity="info"
                                    className="w-full"
                                    loading={requestChangePasswordMutation.isPending}
                                    disabled={requestChangePasswordMutation.isPending}
                                    onClick={() => formik.handleSubmit()}
                                />
                            </div>
                        </Show>
                    </Dialog>

                    {/* Modal de Exclusão */}
                    <Dialog
                        header="Excluir conta"
                        visible={showDeleteModal}
                        style={{ width: "25rem" }}
                        modal
                        onHide={() => setShowDeleteModal(false)}
                    >
                        <p>Tem certeza que deseja excluir sua conta?</p>
                        <p>Essa ação não poderá ser desfeita, tenha certeza de que quer excluir sua conta, todos os seus dados serão perdidos de maneira irreversível.</p>

                        <div className="flex justify-content-end gap-2 mt-4">
                            <Button label="Cancelar" onClick={() => setShowDeleteModal(false)} outlined />
                            <Button 
                                label="Confirmar" 
                                severity="danger" 
                                loading={deleteAccountMutation.isPending} 
                                onClick={async () => {
                                    await deleteAccountMutation.mutateAsync();
                                    toastRef.current?.show({
                                        severity: "success",
                                        summary: "Conta excluída",
                                        detail: "Sua conta foi excluída com sucesso. Deslogando do sistema...",
                                        life: 3000
                                    });
                                    setTimeout(() => {
                                        void auth.logout();
                                    }, 3000);
                                }} 
                                disabled={deleteAccountMutation.isPending}
                            />
                        </div>
                    </Dialog>

                    <Sidebar visible={showEditProfile} position="right" onHide={() => setShowEditProfile(false)}>
                        <InvestorProfileForm />
                    </Sidebar>
                </div>
            </div>
        </>
    );
}
